import request from "@/utils/request";

export interface RoleListItem {
	/**
	 * 角色分组id
	 */
	group_id: number;
	/**
	 * 角色id
	 */
	id: number;
	/**
	 * 角色名称
	 */
	name: string;
	/**
	 * 是否系统内置角色：0-否；1-是
	 * 系统内置角色不允许编辑和删除
	 */
	is_system: number;
}

export interface RoleGroupListItem {
	/**
	 * 角色分组id
	 */
	group_id: number;
	/**
	 * 角色分组名称
	 */
	group_name: string;
	/**
	 * 角色list
	 */
	list: RoleListItem[];
	/**
	 * 是否系统内置角色：0-否；1-是
	 * 系统内置角色不允许编辑和删除
	 */
	is_system: number;
}

export const fetchRoleGroupList = () =>
	request.post<RoleGroupListItem[]>("/authGroup/roleGroupList");

export interface AddRoleGroupRequest {
	group_name: string;
}

export const addRoleGroup = (data: AddRoleGroupRequest) => request.post("/authGroup/add", data);

export interface EditRoleGroupRequest {
	/**
	 * 角色组名称
	 */
	group_name: string;
	/**
	 * 角色组ID
	 */
	id: number;
}

export const editRoleGroup = (data: EditRoleGroupRequest) => request.post("/authGroup/edit", data);

export const deleteRoleGroup = (ids: number[]) => request.post("/authGroup/del", { ids });

export interface AddRoleRequest {
	/**
	 * 角色组ID
	 */
	auth_group_id: number;
	/**
	 * 角色名称
	 */
	name: string;
}

export const addRole = (data: AddRoleRequest) => request.post("/authGroup/addRole", data);

export interface EditRoleRequest {
	/**
	 * 角色ID
	 */
	id: number;
	/**
	 * 角色名称
	 */
	name: string;
	/**
	 * 角色组ID
	 */
	auth_group_id: number;
}

export const editRole = (data: EditRoleRequest) => request.post("/authGroup/editRole", data);

export const deleteRole = (ids: number[]) => request.post("/authGroup/delRole", { ids });

export interface RoleDetail {
	association_id: number;
	/**
	 * 角色组id
	 */
	auth_group_id: number;
	createtime: number;
	data_scope: number;
	/**
	 * 角色id
	 */
	id: number;
	/**
	 * 角色名称
	 */
	name: string;
	pid: number;
	/**
	 * 角色路由id集合
	 */
	rules: string[];
	status: string;
	updatetime: number;
}

export const fetchRoleDetail = (id: number) =>
	request.post<RoleDetail>("/authGroup/detailRole", { id });

export interface FetchRoleUserListRequest extends PaginationParams {
	/**
	 * 角色id
	 */
	id: number;
	/**
	 * 成员名称
	 */
	keyword?: string;
}

export interface RoleUserListItem {
	/**
	 * 成员id
	 */
	admin_id: number;
	/**
	 * 工号
	 */
	admin_sn: string;
	/**
	 * 数据权限范围：1-全部 2-自定义 3-本部门及以下 4-本部门 5-仅本人 6-本企业
	 */
	data_scope: number;
	/**
	 * 邮箱
	 */
	email: string;
	/**
	 * 分组id
	 */
	group_id: number;
	/**
	 * 分组名称
	 */
	group_name: string;
	/**
	 * 手机号
	 */
	mobile: string;
	/**
	 * 操作者
	 */
	operator: string;
	/**
	 * 操作id
	 */
	operator_id: number;
	/**
	 * 姓名
	 */
	realname: string;
	/**
	 * 角色名称
	 */
	role_name: string;
	/**
	 * 拥有权限列表
	 */
	rule_list: string[];
	/**
	 * 账号状态：1-启用；2-禁用
	 */
	status: number;
	/**
	 * 最近更新时间
	 */
	update_at: string;
}

export type FetchRoleUserListResponse = ListDataResponse<RoleUserListItem>;

export const fetchRoleUserList = (data: FetchRoleUserListRequest) =>
	request.post<FetchRoleUserListResponse>("/authGroup/roleUserList", data);

export interface AssignRoleUsersRequest {
	/**
	 * 成员id集合，例如：[1001,1002]
	 */
	admin_ids: number[];
	/**
	 * 角色id
	 */
	id: number;
}

export const assignRoleUsers = (data: AssignRoleUsersRequest) =>
	request.post("/authGroup/assignRoleUsers", data);

export interface AssignRoleRulesRequest {
	/**
	 * 数据权限范围：1全部 2自定义 3本部门及以下
	 */
	data_scope: number;
	/**
	 * 角色id
	 */
	id: number;
	/**
	 * 路由id集合，例如：[1001,1002]
	 */
	rule_ids: number[];
}

export const assignRoleRules = (data: AssignRoleRulesRequest) =>
	request.post("/authGroup/roleBindRules", data);

export interface RoleMenuListItem {
	/**
	 * 下级权限list
	 */
	children?: RoleMenuListItem[];
	/**
	 * 权限icon
	 */
	icon: string;
	/**
	 * 权限id
	 */
	id: number;
	/**
	 * 选中状态：0-未选中；1-全部选中；2-部分选中
	 */
	is_selected: number;
	/**
	 * 上级权限id
	 */
	pid: number;
	/**
	 * 权限名称
	 */
	rule_name: string;
	/**
	 * 权限值
	 */
	rule_value: string;
}

/**
 * 获取角色权限菜单列表
 */
export const fetchRoleMenuList = (data: { id: number }) =>
	request.post<RoleMenuListItem[]>("/authGroup/ruleList", data);

export interface MenuListItem {
	/**
	 * 子菜单列表
	 */
	children: MenuListItem[];
	/**
	 * 菜单icon
	 */
	icon: string;
	/**
	 * 菜单id
	 */
	id: number;
	/**
	 * 是否选择：0-否；1-是
	 */
	is_selected: number;
	/**
	 * 上级菜单id
	 */
	pid: number;
	/**
	 * 菜单名称
	 */
	rule_name: string;
	/**
	 * 菜单标签值
	 */
	rule_value: string;
}

export interface FetchMenuListResponse {
	/**
	 * 按钮标签：目前均是请求api路由凭借'-'所得，例如：authGroup/adminMenuList，则值为authGroup-adminMenuList
	 */
	btn_tags: string[];
	/**
	 * 菜单列表
	 */
	menu_list: MenuListItem[];
}

export interface FetchMenuListRequest {
	/**
	 * 应用编码：oa_zxgroup-OA办公；company_zxgroup-商企中心；order_zxgroup-商订单中心；shop_zxgroup-品牌商城；
	 */
	app_code?: string;
	/**
	 * 应用id（第二个版本使用该id区分菜单）
	 */
	app_id?: string;
}

/**
 * 获取当前用户权限菜单列表
 */
export const fetchMenuList = (data?: FetchMenuListRequest) =>
	request.post<FetchMenuListResponse>("/authGroup/adminMenuList", data);

export interface AdminItem {
	/**
	 * 工号
	 */
	admin_sn: string;
	/**
	 * 企业id
	 */
	association_id: number;
	/**
	 * 部门id
	 */
	dept_id: number;
	/**
	 * 邮箱
	 */
	email: string;
	/**
	 * 成员id
	 */
	id: number;
	/**
	 * 是否主任部门：0-否；1-是
	 */
	is_primary: number;
	/**
	 * 主管id
	 */
	leader_id: number;
	/**
	 * 手机号
	 */
	mobile: string;
	/**
	 * 职位id
	 */
	position_id: number;
	/**
	 * 真实姓名
	 */
	realname: string;
	/**
	 * 状态
	 */
	status: string;
}

export interface AdminListItem {
	/**
	 * 成员列表
	 */
	admin_list: AdminItem[];
	children: AdminListItem[];
	/**
	 * 部门id
	 */
	dept_id: number;
	/**
	 * 部门名称
	 */
	dept_name: string;
	/**
	 * 介绍
	 */
	instro: string;
	/**
	 * 部门主管
	 */
	leader_ids: string;
	/**
	 * 上级id
	 */
	pid: number;
	/**
	 * 部门类型：1-内部企业部门；2-关联外部企业部门
	 */
	type: number;
}

export const fetchAdminList = () => request.post<AdminListItem[]>("/authGroup/adminList");

export interface AssignRoleMembersRequest {
	/**
	 * 成员id集合，例如：[1001,1002]
	 */
	admin_ids: number[];
	/**
	 * 角色id
	 */
	id: number;
}

export const assignRoleMembers = (data: AssignRoleMembersRequest) =>
	request.post("/authGroup/roleBindUser", data);

export interface EditRoleMemberStatusRequest {
	/**
	 * 操作类型：forbid-禁用；del-删除；activate-启用
	 */
	action: "forbid" | "del" | "activate";
	/**
	 * 成员id集合
	 */
	admin_ids: number[];
	/**
	 * 角色id
	 */
	id: number;
}

export const editRoleMemberStatus = (data: EditRoleMemberStatusRequest) =>
	request.post("/authGroup/roleDealUser", data);
